import React, {useEffect, useState} from 'react';
import { observer } from 'mobx-react-lite';
import SimpleTable from '../../components/SimpleTable';
import {Header, Loading} from '../../components';
import {Manager} from "../../core";
import {CustomerStore} from "../../stores/customers/customersStore";
import {Colors} from "../../styles";
import { AiOutlineNotification } from 'react-icons/ai';
import { NotificationsStore } from '../../stores/notifications/notificationsStore';

// helper renderers removed (unused) to satisfy linter

// Função auxiliar para converter UTC do banco para Horário Local
const formatDateTime = (dateStr: string) => {
	if (!dateStr || dateStr === '-') return '-';
	try {
		// Ajusta formato YYYY-MM-DD HH:mm:ss para ISO antes de converter
		const isoStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
		const utcStr = isoStr.endsWith('Z') || isoStr.includes('+') ? isoStr : `${isoStr}Z`;
		const date = new Date(utcStr);
		return date.toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		});
	} catch (e) {
		return dateStr;
	}
};

export const customersGrid = [
	{
		field: 'name',
		headerText: 'Nome',
		width: '100',
		textAlign: 'Center'
	},
	{
		field: 'surname',
		headerText: 'Sobrenome',
		width: '100',
		format: 'yMd',
		textAlign: 'Center',
	},
	{
		field: 'email',
		headerText: 'Email',
		width: '200',
		textAlign: 'Center'
	},
	{
		field: 'cellphone',
		headerText: 'Celular',
		width: '150',
		textAlign: 'Center'
	},

	{
		field: 'createdAt',
		headerText: 'Criado em',
		width: '200',
		textAlign: 'Center',
		template: (row: any) => formatDateTime(row.createdAt),
	},
];


export const Customers = observer(() => {
	const { list, delete: deleteUser } = CustomerStore();
	const [loading, setLoading] = useState(false);
	const [selectedUser, setSelectedUser] = useState<any>(null);

	const [loadingNotify, setLoadingNotify] = useState(false);
	const [notifyTitle, setNotifyTitle] = useState("");
	const [notifyMsg, setNotifyMsg] = useState("");
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);

	useEffect( () => {
		setLoading(true)
		const handlerRequest = async () => {
			try {
				await CustomerStore().get();
				setLoading(false);
			} catch (e) {
				setLoading(false);
				Manager.RequestError('error.customers.handlerRequest', e);
			}
		};

		handlerRequest().then((r) => r);
	}, []);

	useEffect(() => {
		if (!selectedUser) {
			setShowConfirmDelete(false);
		}
	}, [selectedUser]);

	const handleDelete = async (e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		if (!selectedUser) return;
		
		console.log("handleDelete called for user:", selectedUser.id);
		
		try {
			setLoading(true);
			await deleteUser(selectedUser.id);
			console.log("Delete successful");
			setSelectedUser(null);
			setShowConfirmDelete(false);
			setLoading(false);
		} catch (err) {
			console.error("Delete failed:", err);
			setLoading(false);
			Manager.RequestError('error.customers.handleDelete', err);
		}
	};

	const handleSendNotification = async (e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		if (!selectedUser) return;
		const userId = selectedUser.id;
		const fcmId = selectedUser.fcmId;

		if (!userId || !fcmId) {
			alert("Usuário sem identificação para notificação (FCM ID ausente).");
			return;
		}

		if (!notifyTitle.trim() || !notifyMsg.trim()) {
			alert("Por favor, preencha o título e a mensagem da notificação.");
			return;
		}

		setLoadingNotify(true);
		try {
			await NotificationsStore().pushToUser(userId, fcmId, notifyTitle, notifyMsg);
			alert("Notificação enviada com sucesso!");
			setNotifyTitle("");
			setNotifyMsg("");
		} catch (err) {
			console.error(err);
			alert("Falha ao enviar notificação.");
		} finally {
			setLoadingNotify(false);
		}
	};

	return (
		<div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg dark:text-gray-200 rounded-3xl relative">
			{loading && (
				<div className="absolute inset-0 bg-white dark:bg-secondary-dark-bg bg-opacity-50 dark:bg-opacity-80 z-50 flex items-center justify-center">
					<Loading backgroundColor={Colors.light} type={'bubbles'} color={Colors.green_darker} />
				</div>
			)}
			<Header category="Página" title="Clientes"/>
			<SimpleTable 
				columns={customersGrid as any} 
				data={list} 
				pageSize={10} 
				onRowClick={(row) => setSelectedUser(row)}
			/>

			{selectedUser && (
				<div 
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
					onClick={() => setSelectedUser(null)}
				>
					<div 
						className="bg-white dark:bg-[#33373E] p-6 rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6">
							<h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Detalhes do Cliente</h2>
							<button onClick={() => setSelectedUser(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl">&times;</button>
						</div>
						
						<div className="space-y-4 mb-8">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Nome Completo</p>
								<p className="text-lg text-gray-800 dark:text-gray-200">{selectedUser.name} {selectedUser.surname}</p>
							</div>
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Email</p>
								<p className="text-lg text-gray-800 dark:text-gray-200">{selectedUser.email}</p>
							</div>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Celular</p>
									<p className="text-lg text-gray-800 dark:text-gray-200">{selectedUser.cellphone}</p>
								</div>
								<div>
									<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Cadastro</p>
									<p className="text-lg text-gray-800 dark:text-gray-200">{formatDateTime(selectedUser.createdAt)}</p>
								</div>
							</div>

							{(selectedUser.street || selectedUser.city) && (
								<div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
									<h3 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-3">Endereço</h3>
									<div className="grid grid-cols-2 gap-4">
										<div className="col-span-2">
											<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Logradouro</p>
											<p className="text-lg text-gray-800 dark:text-gray-200">
												{selectedUser.street || '-'}
												{selectedUser.number ? `, ${selectedUser.number}` : ''}
											</p>
										</div>
										<div>
											<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Bairro</p>
											<p className="text-lg text-gray-800 dark:text-gray-200">{selectedUser.neighborhood || '-'}</p>
										</div>
										<div>
											<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Cidade / UF</p>
											<p className="text-lg text-gray-800 dark:text-gray-200">
												{selectedUser.city || '-'}{selectedUser.uf ? `/${selectedUser.uf}` : ''}
											</p>
										</div>
										{(selectedUser.apartment || selectedUser.block) && (
											<div>
												<p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Apto / Bloco</p>
												<p className="text-lg text-gray-800 dark:text-gray-200">
													{selectedUser.apartment ? `Apto ${selectedUser.apartment}` : ''}
													{selectedUser.apartment && selectedUser.block ? ' / ' : ''}
													{selectedUser.block ? `Bloco ${selectedUser.block}` : ''}
												</p>
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						{/* SEÇÃO DE NOTIFICAÇÃO */}
						<div className="bg-[#f8f9fa] dark:bg-[#2C3038] border-[#e9ecef] dark:border-gray-700 p-4 rounded-xl border mb-6">
							<div className="text-[#495057] dark:text-gray-200 font-bold mb-4 text-sm flex items-center gap-2">
								{React.createElement(AiOutlineNotification as any, { size: 18 })}
								Notificar o Usuário
							</div>
							<div className="mb-3">
								<label className="text-[#646464] dark:text-gray-400 block text-xs font-semibold mb-1">Título</label>
								<input 
									type="text" 
									value={notifyTitle} 
									onChange={(e) => setNotifyTitle(e.target.value)}
									placeholder="Título da notificação"
									className="w-full bg-white dark:bg-[#20232A] text-[#333] dark:text-gray-200 border-[#ced4da] dark:border-gray-600 p-2 rounded-lg border text-sm focus:outline-none"
								/>
							</div>
							<div className="mb-4">
								<label className="text-[#646464] dark:text-gray-400 block text-xs font-semibold mb-1">Mensagem</label>
								<textarea 
									rows={3}
									value={notifyMsg} 
									onChange={(e) => setNotifyMsg(e.target.value)}
									placeholder="Mensagem da notificação"
									className="w-full bg-white dark:bg-[#20232A] text-[#333] dark:text-gray-200 border-[#ced4da] dark:border-gray-600 p-2 rounded-lg border text-sm focus:outline-none resize-none"
								/>
							</div>
							<button
								onClick={(e) => handleSendNotification(e)}
								disabled={loadingNotify || !notifyTitle.trim() || !notifyMsg.trim()}
								className={`w-full py-2 rounded-lg font-bold text-sm transition-colors ${
									(loadingNotify || !notifyTitle.trim() || !notifyMsg.trim()) 
										? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
										: 'bg-orange-500 hover:bg-orange-600 text-white shadow-md'
								}`}
							>
								{loadingNotify ? 'Enviando...' : 'Enviar Notificação'}
							</button>
						</div>

						<div className="flex gap-3 mt-6">
							{!showConfirmDelete ? (
								<button 
									onClick={(e) => {
										e.stopPropagation();
										setShowConfirmDelete(true);
									}}
									className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-red-200"
								>
									Excluir Usuário
								</button>
							) : (
								<div className="flex-1 flex flex-col gap-2 p-2 border-2 border-red-200 rounded-xl bg-red-50">
									<p className="text-xs text-red-600 font-bold text-center">Confirmar exclusão?</p>
									<div className="flex gap-2">
										<button 
											onClick={(e) => handleDelete(e)}
											className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 rounded-lg"
										>
											Sim, Excluir
										</button>
										<button 
											onClick={(e) => {
												e.stopPropagation();
												setShowConfirmDelete(false);
											}}
											className="flex-1 bg-gray-400 hover:bg-gray-500 text-white text-xs font-bold py-2 rounded-lg"
										>
											Não
										</button>
									</div>
								</div>
							)}
							<button 
								onClick={() => setSelectedUser(null)}
								className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold py-3 rounded-xl transition-colors"
							>
								Fechar
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
});
